import React from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { X } from 'lucide-react';
import { RocketLaunch } from 'components/organisms/jobsDataTable/components/rocketLaunch';

export const HowItWorksModal = ({ onClose, organizationName }) => {
  const steps = [
    {
      title: `${organizationName} boosts your job`,
      description:
        'The team has activated Getro’s AI Agents to promote your open role across their network and beyond.',
    },
    {
      title: 'You share details to optimize results',
      description:
        'Answer a few quick questions about the role and what a great candidate looks like to help the agent attract the best candidates.',
    },
    {
      title: 'You get highly qualified candidates',
      description:
        'Once calibrated, the agent identifies strong profiles, reaches out, and guides interested candidates to apply.',
    },
  ];

  return createPortal(
    <>
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: 'neutral.20',
          opacity: 0.8,
          zIndex: 1000,
        }}
      />
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '539px',
          bg: 'white',
          borderRadius: '4px',
          boxShadow: '0px 1px 4px 0px rgba(27,36,50,0.06), 0px 2px 32px 0px rgba(27,36,50,0.06)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            zIndex: 1002,
          }}
        >
          <Box
            as="button"
            onClick={onClose}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              '&:hover': {
                opacity: 0.7,
              },
            }}
            aria-label="Close"
          >
            <Box as={X} size={24} sx={{ color: 'neutral.300' }} />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
          }}
        >
          <Flex sx={{ gap: '8px', alignItems: 'flex-start' }}>
            <Box
              sx={{
                flexShrink: 0,
                width: '72px',
                height: '72px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RocketLaunch width={72} height={72} />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Text fontSize="20px" fontWeight="semibold" color="text.dark" lineHeight="1.4">
                Attract and convert qualified candidates automatically
              </Text>
              <Text fontSize="16px" color="text.main" lineHeight="1.4">
                {organizationName || 'The team'} is helping you bring the right applicants using Getro&apos;s AI Agents.
              </Text>
            </Box>
          </Flex>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Text fontSize="16px" fontWeight="semibold" color="text.dark" lineHeight="1.4">
              How it works
            </Text>

            <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
              {steps.map((step, index) => (
                <Flex
                  key={step.title}
                  sx={{
                    gap: '8px',
                    alignItems: 'flex-start',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Flex
                    sx={{
                      position: 'relative',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '32px',
                        bg: index === 0 ? 'purple.300' : 'purple.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: '-2px',
                      }}
                    >
                      <Text
                        fontSize="14px"
                        fontWeight="medium"
                        color={index === 0 ? 'purple.100' : 'purple.500'}
                        lineHeight="1.4"
                      >
                        {index + 1}
                      </Text>
                    </Box>
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          width: '4px',
                          height: '100%',
                          bg: index === 0 ? 'purple.300' : 'purple.100',
                          left: '12px',
                          display: 'flex',
                          flex: 1,

                          zIndex: 0,
                        }}
                      />
                    )}
                  </Flex>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      pb: index === steps.length - 1 ? '0px' : '16px',
                    }}
                  >
                    <Text fontSize="14px" fontWeight="medium" color="text.dark" lineHeight="1.4">
                      {step.title}
                    </Text>
                    <Text fontSize="14px" color="text.main" lineHeight="1.4">
                      {step.description}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'neutral.100',
            py: '16px',
            mx: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              bg: 'purple.500',
              color: 'white',
              height: '40px',
              px: '24px',
              py: '8px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'medium',
              lineHeight: '20px',
              '&:hover': {
                bg: 'purple.500',
                opacity: 0.9,
              },
            }}
          >
            Got it
          </Button>
        </Box>
      </Box>
    </>,
    document.getElementById('portals') || document.body,
  );
};

HowItWorksModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  organizationName: PropTypes.string,
};

HowItWorksModal.defaultProps = {
  organizationName: null,
};
